// frontend/src/portal/pages/PortalCompany.jsx
import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { fetchPortalCompany, fetchPortalCompanyDocs, downloadPortalCompanyDoc, uploadPortalDocument, getPortalCompanyDocSignedUrl, openDocumentWithSignedUrl } from "../../api.js";
import { DOC_KINDS, DOC_STATUS_OPTIONS } from "../../docKinds.js";
import { usePortalAuth } from "../components/RequireClient.jsx";
import ErrorBoundary from "../../components/ErrorBoundary.jsx";
import { useNotify } from "../../components/Notify.jsx";
import { FileText, CheckCircle2, AlertCircle, X, ArrowLeft, FileStack, Info } from "lucide-react";

const companyDocKinds = DOC_KINDS.filter((kind) => kind.target === "company" || kind.target === "both");

export default function PortalCompany() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { me, memberRole } = usePortalAuth();
  const notify = useNotify();

  const role = memberRole || me?.role || null;
  const isClientUser = role === "client_user";
  const canUpload = role === "super_admin" || role === "owner";

  const [company, setCompany] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [selectedKind, setSelectedKind] = useState(companyDocKinds[0]?.key || "other");
  const activeTabFromUrl = searchParams.get("tab") || "documents";
  const activeTab = activeTabFromUrl === "overview" ? "overview" : "documents";

  const setActiveTab = (nextTab) => {
    const tab = nextTab === "overview" ? "overview" : "documents";
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    setSearchParams(next, { replace: true });
  };

  const sortedDocs = useMemo(() => {
    return [...docs].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  }, [docs]);

  const docsByCategory = useMemo(() => {
    const categories = {};
    sortedDocs.forEach((doc) => {
      const kind = DOC_KINDS.find((k) => k.key === doc.kind);
      const category = kind?.category || "other";
      if (!categories[category]) categories[category] = [];
      categories[category].push(doc);
    });
    return categories;
  }, [sortedDocs]);

  const metrics = useMemo(() => {
    const total = sortedDocs.length;
    const withPath = sortedDocs.filter((d) => d.path).length;
    const expiringSoon = sortedDocs.filter((d) => {
      if (!d.expires_on) return false;
      const exp = new Date(d.expires_on);
      const now = new Date();
      const days = Math.floor((exp - now) / (1000 * 60 * 60 * 24));
      return days >= 0 && days <= 30;
    }).length;
    const expired = sortedDocs.filter((d) => {
      if (!d.expires_on) return false;
      return new Date(d.expires_on) < new Date();
    }).length;

    return { total, withPath, expiringSoon, expired };
  }, [sortedDocs]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    } catch {
      return dateStr;
    }
  };

  const getExpiryStatus = (doc) => {
    if (!doc.expires_on) return null;
    const exp = new Date(doc.expires_on);
    const now = new Date();
    const days = Math.floor((exp - now) / (1000 * 60 * 60 * 24));
    if (days < 0) return { label: "Expired", color: "var(--danger)", bg: "var(--danger-bg)" };
    if (days <= 30) return { label: `${days}d left`, color: "var(--warning)", bg: "var(--warning-bg)" };
    return null;
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const comp = await fetchPortalCompany(id);
        const normalizedCompany = comp?.company ?? comp;
        if (!normalizedCompany || typeof normalizedCompany !== "object") {
          throw new Error("Company not found");
        }

        const docRows = await fetchPortalCompanyDocs(id);
        const normalizedDocs = Array.isArray(docRows?.docs) ? docRows.docs : Array.isArray(docRows) ? docRows : [];

        if (mounted) {
          setCompany(normalizedCompany);
          setDocs(normalizedDocs);
        }
      } catch (err) {
        console.error("[PortalCompany] Failed to load company", err);
        if (mounted) setError(err instanceof Error ? err : new Error("Unable to load company"));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [id]);

  const refreshDocs = async () => {
    if (!canUpload) return;
    try {
      const rows = await fetchPortalCompanyDocs(id);
      const normalizedDocs = Array.isArray(rows?.docs) ? rows.docs : Array.isArray(rows) ? rows : [];
      setDocs(normalizedDocs);
    } catch (err) {
      setUploadError(err);
    }
  };

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!canUpload) return;
    const form = event.target;
    const data = new FormData(form);
    setUploading(true);
    setUploadError(null);
    try {
      await uploadPortalDocument(id, data);
      form.reset();
      setSelectedKind(companyDocKinds[0]?.key || "other");
      await refreshDocs();
    } catch (err) {
      setUploadError(err);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      await openDocumentWithSignedUrl(() => getPortalCompanyDocSignedUrl(id, doc.id), {
        notify,
        fallbackDownload: () => downloadPortalCompanyDoc(id, doc.id),
      });
    } catch (err) {
      console.error("[handleDownload] Error:", err);
      setUploadError(err);
    }
  };

  if (loading) {
    return (
      <ErrorBoundary componentName="PortalCompany">
        <div style={{ minHeight: "100vh", background: "var(--bg-secondary)", padding: "var(--space-2xl)" }}>
          <div style={{ maxWidth: "var(--container-max)", margin: "0 auto", textAlign: "center", paddingTop: "48px" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                border: "3px solid var(--border-primary)",
                borderTopColor: "var(--accent-primary)",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
                margin: "0 auto var(--space-lg)",
              }}
            />
            <p style={{ color: "var(--text-muted)" }}>Loading company…</p>
          </div>
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </ErrorBoundary>
    );
  }

  if (error) {
    return (
      <ErrorBoundary componentName="PortalCompany">
        <div style={{ minHeight: "100vh", background: "var(--bg-secondary)", padding: "var(--space-2xl)" }}>
          <div style={{ maxWidth: "var(--container-max)", margin: "0 auto" }}>
            <div
              style={{
                backgroundColor: "var(--surface)",
                borderRadius: "var(--radius-lg)",
                padding: "var(--space-2xl)",
                textAlign: "center",
                border: "1px solid var(--border-primary)",
              }}>
              <AlertCircle size={48} style={{ color: "var(--danger)", marginBottom: "16px" }} />
              <h2 style={{ fontSize: "20px", fontWeight: "600", color: "var(--text-primary)", marginBottom: "var(--space-md)" }}>Unable to load company</h2>
              <p style={{ color: "var(--text-secondary)", marginBottom: "var(--space-lg)" }}>{error.message || "Try again later."}</p>
              <Link
                to="/portal/overview"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "var(--space-md) var(--space-xl)",
                  backgroundColor: "var(--accent-primary)",
                  color: "#fff",
                  textDecoration: "none",
                  borderRadius: "var(--radius-md)",
                  fontSize: "14px",
                  fontWeight: "500",
                }}>
                <ArrowLeft size={16} />
                Back to overview
              </Link>
            </div>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  if (!company) {
    return (
      <ErrorBoundary componentName="PortalCompany">
        <div style={{ minHeight: "100vh", background: "var(--bg-secondary)", padding: "var(--space-2xl)" }}>
          <div style={{ maxWidth: "var(--container-max)", margin: "0 auto" }}>
            <div
              style={{
                backgroundColor: "var(--surface)",
                borderRadius: "var(--radius-lg)",
                padding: "var(--space-2xl)",
                textAlign: "center",
                border: "1px solid var(--border-primary)",
              }}>
              <FileStack size={48} style={{ color: "var(--text-muted)", marginBottom: "16px" }} />
              <h2 style={{ fontSize: "20px", fontWeight: "600", color: "var(--text-primary)", marginBottom: "var(--space-md)" }}>Company unavailable</h2>
              <p style={{ color: "var(--text-secondary)", marginBottom: "var(--space-lg)" }}>We could not load this company right now. Please try again later.</p>
              <Link
                to="/portal/overview"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "var(--space-md) var(--space-xl)",
                  backgroundColor: "var(--accent-primary)",
                  color: "#fff",
                  textDecoration: "none",
                  borderRadius: "var(--radius-md)",
                  fontSize: "14px",
                  fontWeight: "500",
                }}>
                <ArrowLeft size={16} />
                Back to overview
              </Link>
            </div>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  const statusColors = {
    active: { bg: "var(--success-bg)", text: "var(--success)" },
    pending: { bg: "var(--warning-bg)", text: "var(--warning)" },
    pending_gov_approval: { bg: "var(--warning-bg)", text: "var(--warning)" },
    inactive: { bg: "var(--danger-bg)", text: "var(--danger)" },
  };
  const statusColor = statusColors[company.status] || { bg: "var(--surface-elevated)", text: "var(--text-muted)" };

  return (
    <ErrorBoundary componentName="PortalCompany">
      <div style={{ minHeight: "100vh", background: "var(--bg-secondary)" }}>
        {/* Header with company overview */}
        <div style={{ background: "var(--surface)", borderBottom: "1px solid var(--border-primary)" }}>
          <div style={{ maxWidth: "var(--container-max-wide)", margin: "0 auto", padding: "var(--space-xl) var(--space-xl) 0" }}>
            <Link
              to="/portal/overview"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                color: "var(--text-secondary)",
                textDecoration: "none",
                fontSize: "14px",
                marginBottom: "var(--space-lg)",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--accent-primary)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}>
              <ArrowLeft size={16} />
              Back to overview
            </Link>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "var(--space-xl)" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
                  <h1 style={{ fontSize: "28px", fontWeight: "500", color: "var(--text-primary)", margin: 0 }}>{company.name}</h1>
                  <span
                    style={{
                      padding: "4px 12px",
                      borderRadius: "var(--radius-sm)",
                      fontSize: "13px",
                      fontWeight: "600",
                      backgroundColor: statusColor.bg,
                      color: statusColor.text,
                      textTransform: "capitalize",
                    }}>
                    {company.status?.replace(/_/g, " ")}
                  </span>
                </div>
                <div style={{ display: "flex", gap: "24px", fontSize: "14px", color: "var(--text-secondary)" }}>
                  {company.registration_no && <span>Reg: {company.registration_no}</span>}
                  {company.created_at && <span>Created: {formatDate(company.created_at)}</span>}
                  {company.updated_at && <span>Last updated: {formatDate(company.updated_at)}</span>}
                </div>
              </div>
            </div>

            {/* Metrics cards */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "var(--space-md)",
                marginBottom: "var(--space-xl)",
              }}>
              <MetricCard icon={FileText} label="Total Documents" value={metrics.total} />
              <MetricCard icon={CheckCircle2} label="Uploaded" value={metrics.withPath} subtext={`${metrics.total - metrics.withPath} pending`} />
              <MetricCard icon={AlertCircle} label="Expiring Soon" value={metrics.expiringSoon} alert={metrics.expiringSoon > 0} />
              <MetricCard icon={X} label="Expired" value={metrics.expired} alert={metrics.expired > 0} />
            </div>

            {/* Tabs */}
            <div style={{ display: "flex", gap: "var(--space-sm)", borderBottom: "1px solid var(--border-primary)" }}>
              <TabButton label="Documents" active={activeTab === "documents"} onClick={() => setActiveTab("documents")} />
              <TabButton label="Overview" active={activeTab === "overview"} onClick={() => setActiveTab("overview")} />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ maxWidth: "var(--container-max-wide)", margin: "0 auto", padding: "var(--space-2xl) var(--space-xl)" }}>
          {isClientUser && (
            <div
              style={{
                backgroundColor: "var(--info-bg)",
                border: "1px solid var(--info)",
                borderRadius: "var(--radius-md)",
                padding: "var(--space-lg)",
                marginBottom: "var(--space-2xl)",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}>
              <Info size={20} style={{ color: "var(--info)", flexShrink: 0 }} />
              <p style={{ margin: 0, color: "var(--info)", fontSize: "14px" }}>This view is read-only. To manage documents, contact your administrator.</p>
            </div>
          )}

          {activeTab === "documents" && (
            <>
              {canUpload && (
                <div
                  style={{
                    backgroundColor: "var(--surface)",
                    borderRadius: "var(--radius-lg)",
                    border: "1px solid var(--border-primary)",
                    padding: "var(--space-xl)",
                    marginBottom: "var(--space-2xl)",
                  }}>
                  <h2 style={{ fontSize: "18px", fontWeight: "600", color: "var(--text-primary)", margin: "0 0 var(--space-lg) 0" }}>Upload Document</h2>

                  <form onSubmit={handleUpload} style={{ display: "grid", gap: "var(--space-lg)" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-md)" }}>
                      <div>
                        <label style={{ display: "block", fontSize: "13px", fontWeight: "500", color: "var(--text-secondary)", marginBottom: "6px" }}>Document Type</label>
                        <select
                          name="kind"
                          required
                          value={selectedKind}
                          onChange={(e) => setSelectedKind(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "10px 12px",
                            border: "1px solid var(--border-primary)",
                            borderRadius: "var(--radius-md)",
                            fontSize: "14px",
                            backgroundColor: "var(--surface)",
                            color: "var(--text-primary)",
                          }}>
                          {companyDocKinds.map((kind) => (
                            <option key={kind.key} value={kind.key}>
                              {kind.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {selectedKind === "other" && (
                        <div>
                          <label style={{ display: "block", fontSize: "13px", fontWeight: "500", color: "var(--text-secondary)", marginBottom: "6px" }}>Label</label>
                          <input
                            name="kind_label"
                            type="text"
                            required
                            style={{
                              width: "100%",
                              padding: "10px 12px",
                              border: "1px solid var(--border-primary)",
                              borderRadius: "var(--radius-md)",
                              fontSize: "14px",
                              backgroundColor: "var(--surface)",
                              color: "var(--text-primary)",
                            }}
                          />
                        </div>
                      )}

                      <div>
                        <label style={{ display: "block", fontSize: "13px", fontWeight: "500", color: "var(--text-secondary)", marginBottom: "6px" }}>Identifier</label>
                        <input
                          name="identifier"
                          type="text"
                          placeholder="e.g., License #123"
                          style={{
                            width: "100%",
                            padding: "10px 12px",
                            border: "1px solid var(--border-primary)",
                            borderRadius: "var(--radius-md)",
                            fontSize: "14px",
                            backgroundColor: "var(--surface)",
                            color: "var(--text-primary)",
                          }}
                        />
                      </div>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "var(--space-md)" }}>
                      <div>
                        <label style={{ display: "block", fontSize: "13px", fontWeight: "500", color: "var(--text-secondary)", marginBottom: "6px" }}>Issued On</label>
                        <input
                          name="issued_on"
                          type="date"
                          style={{
                            width: "100%",
                            padding: "10px 12px",
                            border: "1px solid var(--border-primary)",
                            borderRadius: "var(--radius-md)",
                            fontSize: "14px",
                            backgroundColor: "var(--surface)",
                            color: "var(--text-primary)",
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ display: "block", fontSize: "13px", fontWeight: "500", color: "var(--text-secondary)", marginBottom: "6px" }}>Expires On</label>
                        <input
                          name="expires_on"
                          type="date"
                          style={{
                            width: "100%",
                            padding: "10px 12px",
                            border: "1px solid var(--border-primary)",
                            borderRadius: "var(--radius-md)",
                            fontSize: "14px",
                            backgroundColor: "var(--surface)",
                            color: "var(--text-primary)",
                          }}
                        />
                      </div>

                      <div>
                        <label style={{ display: "block", fontSize: "13px", fontWeight: "500", color: "var(--text-secondary)", marginBottom: "6px" }}>Status</label>
                        <select
                          name="status"
                          style={{
                            width: "100%",
                            padding: "10px 12px",
                            border: "1px solid var(--border-primary)",
                            borderRadius: "var(--radius-md)",
                            fontSize: "14px",
                            backgroundColor: "var(--surface)",
                            color: "var(--text-primary)",
                          }}>
                          <option value="">Select status</option>
                          {DOC_STATUS_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label style={{ display: "block", fontSize: "13px", fontWeight: "500", color: "var(--text-secondary)", marginBottom: "6px" }}>File</label>
                        <input
                          name="file"
                          type="file"
                          accept=".pdf,image/*"
                          required
                          style={{
                            width: "100%",
                            padding: "10px 12px",
                            border: "1px solid var(--border-primary)",
                            borderRadius: "var(--radius-md)",
                            fontSize: "14px",
                            backgroundColor: "var(--surface)",
                            color: "var(--text-primary)",
                          }}
                        />
                      </div>
                    </div>

                    {uploadError && (
                      <div
                        style={{
                          padding: "12px 16px",
                          backgroundColor: "var(--danger-bg)",
                          border: "1px solid var(--danger)",
                          borderRadius: "var(--radius-md)",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}>
                        <X size={16} style={{ color: "var(--danger)" }} />
                        <p style={{ color: "var(--danger)", fontSize: "14px", margin: 0 }}>{uploadError.message || "Upload failed"}</p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={uploading}
                      style={{
                        padding: "12px 24px",
                        backgroundColor: uploading ? "var(--text-disabled)" : "var(--accent-primary)",
                        color: "#fff",
                        border: "none",
                        borderRadius: "var(--radius-md)",
                        fontSize: "14px",
                        fontWeight: "600",
                        cursor: uploading ? "not-allowed" : "pointer",
                        width: "fit-content",
                      }}>
                      {uploading ? "Uploading…" : "Upload Document"}
                    </button>
                  </form>
                </div>
              )}

              {/* Documents by category */}
              {Object.keys(docsByCategory).length === 0 ? (
                <div
                  style={{
                    backgroundColor: "var(--surface)",
                    borderRadius: "var(--radius-lg)",
                    border: "1px solid var(--border-primary)",
                    padding: "var(--space-2xl)",
                    textAlign: "center",
                  }}>
                  <FileStack size={48} style={{ color: "var(--text-muted)", marginBottom: "16px" }} />
                  <p style={{ color: "var(--text-muted)", margin: "16px 0 0" }}>No documents uploaded yet.</p>
                </div>
              ) : (
                Object.entries(docsByCategory).map(([category, categoryDocs]) => (
                  <div
                    key={category}
                    style={{
                      backgroundColor: "var(--surface)",
                      borderRadius: "var(--radius-lg)",
                      border: "1px solid var(--border-primary)",
                      overflow: "hidden",
                      marginBottom: "var(--space-lg)",
                    }}>
                    <div style={{ padding: "var(--space-lg)", borderBottom: "1px solid var(--border-primary)", backgroundColor: "var(--surface-elevated)" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "600", color: "var(--text-primary)", margin: 0, textTransform: "capitalize" }}>
                        {category} ({categoryDocs.length})
                      </h3>
                    </div>

                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ backgroundColor: "var(--surface-elevated)", borderBottom: "1px solid var(--border-primary)" }}>
                            <th
                              style={{ padding: "12px 16px", textAlign: "left", fontSize: "12px", fontWeight: "600", color: "var(--text-secondary)", textTransform: "uppercase" }}>
                              Type
                            </th>
                            <th
                              style={{ padding: "12px 16px", textAlign: "left", fontSize: "12px", fontWeight: "600", color: "var(--text-secondary)", textTransform: "uppercase" }}>
                              Identifier
                            </th>
                            <th
                              style={{ padding: "12px 16px", textAlign: "left", fontSize: "12px", fontWeight: "600", color: "var(--text-secondary)", textTransform: "uppercase" }}>
                              Issued
                            </th>
                            <th
                              style={{ padding: "12px 16px", textAlign: "left", fontSize: "12px", fontWeight: "600", color: "var(--text-secondary)", textTransform: "uppercase" }}>
                              Expires
                            </th>
                            <th
                              style={{ padding: "12px 16px", textAlign: "left", fontSize: "12px", fontWeight: "600", color: "var(--text-secondary)", textTransform: "uppercase" }}>
                              Status
                            </th>
                            <th
                              style={{ padding: "12px 16px", textAlign: "right", fontSize: "12px", fontWeight: "600", color: "var(--text-secondary)", textTransform: "uppercase" }}>
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {categoryDocs.map((doc) => {
                            const expiryStatus = getExpiryStatus(doc);
                            return (
                              <tr key={doc.id} style={{ borderBottom: "1px solid var(--border-primary)" }}>
                                <td style={{ padding: "14px 16px", fontSize: "14px", color: "var(--text-primary)", fontWeight: "500" }}>{doc.kind}</td>
                                <td style={{ padding: "14px 16px", fontSize: "14px", color: "var(--text-secondary)" }}>{doc.identifier || "—"}</td>
                                <td style={{ padding: "14px 16px", fontSize: "13px", color: "var(--text-secondary)" }}>{formatDate(doc.issued_on)}</td>
                                <td style={{ padding: "14px 16px", fontSize: "13px" }}>
                                  {expiryStatus ? (
                                    <span
                                      style={{
                                        padding: "4px 8px",
                                        backgroundColor: expiryStatus.bg,
                                        color: expiryStatus.color,
                                        borderRadius: "var(--radius-sm)",
                                        fontSize: "12px",
                                        fontWeight: "600",
                                      }}>
                                      {expiryStatus.label}
                                    </span>
                                  ) : (
                                    <span style={{ color: "var(--text-secondary)" }}>{formatDate(doc.expires_on)}</span>
                                  )}
                                </td>
                                <td style={{ padding: "14px 16px", fontSize: "13px" }}>
                                  <span
                                    style={{
                                      padding: "4px 10px",
                                      backgroundColor: "var(--success-bg)",
                                      color: "var(--success)",
                                      borderRadius: "var(--radius-sm)",
                                      fontSize: "12px",
                                      fontWeight: "600",
                                      textTransform: "capitalize",
                                    }}>
                                    {doc.status || "received"}
                                  </span>
                                </td>
                                <td style={{ padding: "14px 16px", textAlign: "right" }}>
                                  {doc.path ? (
                                    <button
                                      type="button"
                                      onClick={() => handleDownload(doc)}
                                      style={{
                                        padding: "8px 16px",
                                        backgroundColor: "var(--accent-primary)",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: "var(--radius-sm)",
                                        fontSize: "13px",
                                        fontWeight: "500",
                                        cursor: "pointer",
                                        transition: "opacity 0.2s",
                                      }}
                                      onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                                      onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}>
                                      Download
                                    </button>
                                  ) : (
                                    <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>Pending upload</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))
              )}
            </>
          )}

          {activeTab === "overview" && (
            <div
              style={{
                backgroundColor: "var(--surface)",
                borderRadius: "var(--radius-lg)",
                border: "1px solid var(--border-primary)",
                padding: "var(--space-2xl)",
              }}>
              <h2 style={{ fontSize: "18px", fontWeight: "600", color: "var(--text-primary)", margin: "0 0 var(--space-xl) 0" }}>Company Information</h2>

              <div style={{ display: "grid", gap: "var(--space-lg)" }}>
                <InfoRow label="Legal Name" value={company.name} />
                <InfoRow label="Registration Number" value={company.registration_no} />
                <InfoRow label="Legal Form" value={company.legal_form} />
                <InfoRow label="Status" value={company.status?.replace(/_/g, " ")} />
                <InfoRow label="Business Activity" value={company.business_activity} />
                <InfoRow label="Created" value={formatDate(company.created_at)} />
                <InfoRow label="Last Updated" value={formatDate(company.updated_at)} />
              </div>

              <div
                style={{
                  marginTop: "var(--space-xl)",
                  padding: "var(--space-lg)",
                  backgroundColor: "var(--info-bg)",
                  border: "1px solid var(--info)",
                  borderRadius: "var(--radius-md)",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}>
                <Info size={20} style={{ color: "var(--info)", flexShrink: 0 }} />
                <p style={{ margin: 0, color: "var(--info)", fontSize: "14px" }}>To edit company details, please use the main CBCFX application.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
}

function MetricCard({ icon: Icon, label, value, subtext, alert }) {
  return (
    <div
      style={{
        backgroundColor: "var(--surface)",
        border: `1px solid ${alert ? "var(--warning)" : "var(--border-primary)"}`,
        borderRadius: "var(--radius-md)",
        padding: "var(--space-lg)",
      }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
        <Icon size={20} style={{ color: alert ? "var(--warning)" : "var(--accent-primary)" }} />
        <span style={{ fontSize: "12px", fontWeight: "600", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
      </div>
      <div style={{ fontSize: "24px", fontWeight: "600", color: alert ? "var(--warning)" : "var(--text-primary)" }}>{value}</div>
      {subtext && <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "4px" }}>{subtext}</div>}
    </div>
  );
}

function TabButton({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "12px 24px",
        border: "none",
        backgroundColor: active ? "var(--accent-primary)" : "transparent",
        color: active ? "#fff" : "var(--text-primary)",
        fontSize: "14px",
        fontWeight: active ? "600" : "500",
        cursor: "pointer",
        borderBottom: "none",
        borderRadius: "var(--radius-md) var(--radius-md) 0 0",
        transition: "all 0.2s",
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.backgroundColor = "var(--surface-hover)";
          e.currentTarget.style.color = "var(--accent-primary)";
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.backgroundColor = "transparent";
          e.currentTarget.style.color = "var(--text-primary)";
        }
      }}>
      {label}
    </button>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: "flex", borderBottom: "1px solid var(--border-primary)", paddingBottom: "12px" }}>
      <div style={{ flex: "0 0 200px", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      <div style={{ flex: 1, fontSize: "14px", color: "var(--text-primary)", textTransform: "capitalize" }}>{value || "—"}</div>
    </div>
  );
}
